import { motion } from 'framer-motion';
import { Activity, AlertTriangle, Shield } from 'lucide-react';

// Simulation Result Card — extracted from DesignStudio.jsx (Feb 2026 refactor)
export default function SimulationCard({ result, language }) {
  if (!result) return null;

  const analysis = result.analysis || result;
  const riskColors = {
    low: 'text-green-400 bg-green-500/20',
    medium: 'text-yellow-400 bg-yellow-500/20',
    high: 'text-red-400 bg-red-500/20',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-900/80 border border-slate-700 rounded-xl p-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          <span className="font-semibold text-white capitalize">{analysis.simulation_type || 'Analysis'}</span>
        </div>
        {analysis.risk_level && (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${riskColors[analysis.risk_level] || riskColors.medium}`}>
            {analysis.risk_level.toUpperCase()}
          </span>
        )}
      </div>

      {analysis.overall_score && (
        <div className="flex items-center gap-4">
          <div className="flex-1 h-3 bg-slate-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${analysis.overall_score}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className={`h-full rounded-full ${
                analysis.overall_score >= 80 ? 'bg-green-500' :
                analysis.overall_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
              }`}
            />
          </div>
          <span className="text-white font-bold">{analysis.overall_score}%</span>
        </div>
      )}

      {analysis.summary && (
        <p className="text-slate-300 text-sm">{analysis.summary}</p>
      )}

      {analysis.metrics?.primary_metric && (
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <span className="text-slate-400 text-xs">{analysis.metrics.primary_metric.name}</span>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-xl font-bold text-white">{analysis.metrics.primary_metric.value}</span>
              <span className="text-xs text-slate-500">{analysis.metrics.primary_metric.unit}</span>
            </div>
          </div>
          {analysis.metrics.secondary_metrics?.slice(0, 3).map((metric, i) => (
            <div key={i} className="bg-slate-800/50 rounded-lg p-3">
              <span className="text-slate-400 text-xs">{metric.name}</span>
              <div className="flex items-baseline gap-1 mt-1">
                <span className="text-lg font-bold text-white">{metric.value}</span>
                <span className="text-xs text-slate-500">{metric.unit || ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {analysis.component_analysis && analysis.component_analysis.length > 0 && (
        <div className="space-y-2">
          <span className="text-slate-400 text-xs font-medium">
            {language === 'tr' ? 'Bileşen Analizi' : 'Component Analysis'}
          </span>
          <div className="space-y-1">
            {analysis.component_analysis.slice(0, 6).map((comp, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: comp.risk_color || '#4CAF50' }} />
                <span className="text-slate-300 text-xs flex-1 truncate">{comp.component_name}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  comp.status === 'safe' ? 'text-green-400 bg-green-500/20' :
                  comp.status === 'warning' ? 'text-yellow-400 bg-yellow-500/20' :
                  'text-red-400 bg-red-500/20'
                }`}>
                  {comp.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {analysis.critical_points && analysis.critical_points.length > 0 && (
        <div className="space-y-2">
          <span className="text-slate-400 text-xs font-medium flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {language === 'tr' ? 'Kritik Noktalar' : 'Critical Points'}
          </span>
          {analysis.critical_points.slice(0, 3).map((point, i) => (
            <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-lg p-2">
              <div className="flex items-center justify-between">
                <span className="text-red-300 text-xs font-medium">{point.component}</span>
                <span className={`text-xs ${
                  point.severity === 'high' ? 'text-red-400' :
                  point.severity === 'medium' ? 'text-yellow-400' : 'text-green-400'
                }`}>{point.severity}</span>
              </div>
              <p className="text-slate-400 text-xs mt-1">{point.issue}</p>
              {point.recommendation && (
                <p className="text-blue-400 text-xs mt-1">→ {point.recommendation}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div className="space-y-1">
          <span className="text-slate-400 text-xs font-medium">
            {language === 'tr' ? 'Öneriler' : 'Recommendations'}
          </span>
          {analysis.recommendations.slice(0, 3).map((rec, i) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <Shield className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
              <span className="text-slate-300">
                {typeof rec === 'object' ? rec.action : rec}
              </span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
